import { uiText } from '../../utils/i18n';
import { useCallback, useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import styles from '../OntologyPage.module.css';
import type { PageView } from './types';

interface TourStep {
  eyebrow: string;
  title: string;
  description: string;
  bullets?: string[];
  target?: string;
  view?: PageView;
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TOUR_STEPS: TourStep[] = [
  {
    eyebrow: 'WELCOME TO BUSINESS ONTOLOGY',
    title: '用 1 分钟看懂业务本体',
    description: '本体不是另一套知识库。它把业务里的对象、指标、数据和负责人连接成一张可推理的地图，让 Agent 不只“查到资料”，还知道一个数字怎么来、为什么变、会影响什么。',
  },
  {
    eyebrow: '01 · FOUR LAYERS',
    title: '先看懂四层业务地图',
    description: '四层不是四个独立目录，而是一条从事实到行动的链路。你不需要一次填满，先搭出一条最重要的链路即可。',
    bullets: ['数据层：数从哪里来', '指标层：数字怎么计算', '业务层：数字说明什么', '执行层：谁根据它采取行动'],
    target: '[data-tour="layer-map"]',
    view: 'overview',
  },
  {
    eyebrow: '02 · SIX WORKSPACES',
    title: '六个视图，其实是同一张图',
    description: '业务地图看全貌，指标网络看口径与上下游，数据血缘看来源，影响分析看原因和后果，组织执行看负责人，模型定义管理对象类型。切换视图不会产生重复数据。',
    target: '[data-tour="view-tabs"]',
    view: 'overview',
  },
  {
    eyebrow: '03 · START WITH A METRIC',
    title: '建议从一个核心指标开始',
    description: '先创建“销售额、订单量、转化率”这类真正用于决策的指标，补齐业务定义、公式、统计粒度和负责人；再记录至少两个周期的数值，NovaX 才能判断它发生了什么变化。',
    bullets: ['定义指标口径', '记录两个以上周期', '连接计算来源和业务目标'],
    target: '[data-tour="metric-workspace"]',
    view: 'metrics',
  },
  {
    eyebrow: '04 · DATA LINEAGE',
    title: '把指标接回真实数据',
    description: '登记数据库、数据表和关键字段，再用“数据血缘”或“计算依赖”连接到指标。以后 Agent 被问到口径时，就能沿链路找到真实来源，而不是根据文档猜测。',
    target: '[data-tour="lineage-view"]',
    view: 'lineage',
  },
  {
    eyebrow: '05 · IMPACT ANALYSIS',
    title: '沿关系找原因，也看后果',
    description: '选择一个指标后，影响分析会向上查找计算来源和候选驱动，向下查找会受影响的目标、流程与负责人。影响关系只是线索；只有带有验证证据的因果关系，Agent 才会作为确定原因表达。',
    target: '[data-tour="impact-view"]',
    view: 'impact',
  },
  {
    eyebrow: '06 · OBJECTS & RELATIONS',
    title: '使用时只记住两个动作',
    description: '先“新建对象”，再“建立关系”。对象可以是字段、指标、目标或 Agent；关系需要明确选择计算、血缘、影响、因果或归属，并尽量补充公式、条件、置信度和证据。',
    target: '[data-tour="header-actions"]',
    view: 'overview',
  },
  {
    eyebrow: '07 · AGENT REASONING',
    title: '结构搭好后，Agent 会自动使用',
    description: '当你询问指标、血缘、归因或影响时，NovaX Agent 会先读取相关本体上下文。知识库提供“原文与背景”，本体提供“确定的业务结构”，两者可以通过证据关联起来。',
    target: '[data-tour="agent-reasoning"]',
    view: 'overview',
  },
  {
    eyebrow: 'READY TO BUILD',
    title: '现在搭建第一条业务链路',
    description: '最小可用链路只需要四个对象。先从你每天都会关注的那个核心指标开始，后面再逐步补全，不需要一开始设计整个公司。',
    bullets: ['数据字段 → 核心指标 → 业务目标 → 负责人'],
    view: 'overview',
  },
];

export function OntologyTour({
  pageView,
  onViewChange,
  onDismiss,
  onStartMetric,
}: {
  pageView: PageView;
  onViewChange: (view: PageView) => void;
  onDismiss: () => void;
  onStartMetric: () => void;
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [highlight, setHighlight] = useState<HighlightRect | null>(null);
  const step = TOUR_STEPS[stepIndex];
  const isLast = stepIndex === TOUR_STEPS.length - 1;

  useEffect(() => {
    if (step.view && step.view !== pageView) onViewChange(step.view);
  }, [pageView, step.view, onViewChange]);

  const measureTarget = useCallback(() => {
    if (!step.target) {
      setHighlight(null);
      return;
    }
    const element = document.querySelector<HTMLElement>(step.target);
    if (!element) {
      setHighlight(null);
      return;
    }
    const rect = element.getBoundingClientRect();
    const padding = 8;
    const top = Math.max(8, rect.top - padding);
    const left = Math.max(8, rect.left - padding);
    const right = Math.min(window.innerWidth - 8, rect.right + padding);
    const bottom = Math.min(window.innerHeight - 8, rect.bottom + padding);
    setHighlight({ top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) });
  }, [step.target]);

  useLayoutEffect(() => {
    if (step.view && step.view !== pageView) return;
    let frame = 0;
    let timer = 0;
    const element = step.target ? document.querySelector<HTMLElement>(step.target) : null;
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    frame = window.requestAnimationFrame(() => {
      measureTarget();
      timer = window.setTimeout(measureTarget, 260);
    });
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget, pageView, step.target, step.view]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
      if (event.key === 'ArrowLeft' && stepIndex > 0) setStepIndex((value) => value - 1);
      if (event.key === 'ArrowRight' && !isLast) setStepIndex((value) => value + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLast, onDismiss, stepIndex]);

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const cardWidth = Math.min(390, Math.max(280, viewportWidth - 32));
  const estimatedCardHeight = step.bullets ? 330 : 285;
  let cardStyle: CSSProperties = {
    width: cardWidth,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
  if (highlight) {
    const gap = 18;
    const roomRight = viewportWidth - (highlight.left + highlight.width) - gap - 16;
    const roomLeft = highlight.left - gap - 16;
    if (roomRight >= cardWidth || roomLeft >= cardWidth) {
      const left = roomRight >= cardWidth
        ? highlight.left + highlight.width + gap
        : highlight.left - cardWidth - gap;
      const top = Math.max(16, Math.min(viewportHeight - estimatedCardHeight - 16, highlight.top + (highlight.height - estimatedCardHeight) / 2));
      cardStyle = { width: cardWidth, top, left };
    } else {
      const below = highlight.top + highlight.height + gap;
      const top = below + estimatedCardHeight <= viewportHeight - 16
        ? below
        : Math.max(16, highlight.top - estimatedCardHeight - gap);
      const left = Math.max(16, Math.min(viewportWidth - cardWidth - 16, highlight.left + (highlight.width - cardWidth) / 2));
      cardStyle = { width: cardWidth, top, left };
    }
  }

  return <div className={styles.tourRoot} role="dialog" aria-modal="true" aria-labelledby="ontology-tour-title">
    {highlight ? <div className={styles.tourSpotlight} style={highlight} /> : <div className={styles.tourMask} />}
    <section className={styles.tourCard} style={cardStyle}>
      <div className={styles.tourCardTop}>
        <span className={styles.tourEyebrow}>{uiText(step.eyebrow)}</span>
        <button onClick={onDismiss} aria-label={uiText("关闭使用引导")}><span /><span /></button>
      </div>
      <div className={styles.tourIllustration} aria-hidden="true"><i /><i /><i /><b>O</b></div>
      <h2 id="ontology-tour-title">{uiText(step.title)}</h2>
      <p>{uiText(step.description)}</p>
      {step.bullets && <ul className={styles.tourBullets}>{step.bullets.map((item) => <li key={item}><span>✓</span>{uiText(item)}</li>)}</ul>}
      <div className={styles.tourProgress} aria-label={`${uiText("引导进度")} ${stepIndex + 1}/${TOUR_STEPS.length}`}>
        {TOUR_STEPS.map((item, index) => <i key={item.eyebrow} className={index === stepIndex ? styles.tourProgressActive : index < stepIndex ? styles.tourProgressDone : ''} />)}
      </div>
      <footer className={styles.tourFooter}>
        <button className={styles.tourSkip} onClick={onDismiss}>{uiText("跳过引导")}</button>
        <div>
          {stepIndex > 0 && <button className={styles.tourBack} onClick={() => setStepIndex((value) => value - 1)}>{uiText("上一步")}</button>}
          {isLast
            ? <button className={styles.tourNext} onClick={onStartMetric}>{uiText("创建第一个指标")}</button>
            : <button className={styles.tourNext} onClick={() => setStepIndex((value) => value + 1)}>{uiText("下一步")} <span>→</span></button>}
        </div>
      </footer>
      <small className={styles.tourCounter}>{String(stepIndex + 1).padStart(2, '0')} / {String(TOUR_STEPS.length).padStart(2, '0')}</small>
    </section>
  </div>;
}
